import React, { createContext, useCallback } from "react";

import { Auth } from "aws-amplify";
import { httpGet, httpPatch } from "../http";
import { USERS_FEE_ENDPOINT } from "../constants/API_ENDPOINT";
import { useEthosNotification } from "./ethos-notification-context";

const UserFeeContext = createContext(null);

function UserFeeProvider(props) {
  const { registerPendingRequest, unregisterPendingRequest } =
    useEthosNotification();

  const getFeeUsers = useCallback(
    async (search = null, filter = null, limit = null, offset = 0) => {
      let obj = {
        data: null,
        error: null,
        meta: null,
        status: null,
      };

      try {
        registerPendingRequest();
        const jwtToken = (await Auth.currentSession())
          .getIdToken()
          .getJwtToken();
        const result = await httpGet(`${USERS_FEE_ENDPOINT}`, jwtToken, {
          filter: filter,
          search: search,
          limit: limit,
          offset: offset,
        });

        obj = {
          data: result.data,
          error: null,
          meta: null,
          status: result.status,
        };
      } catch (err) {
        obj = {
          data: null,
          error: err,
          meta: null,
          status: null,
        };
      } finally {
        unregisterPendingRequest();
      }

      return obj;
    },
    [registerPendingRequest, unregisterPendingRequest],
  );

  const getFeeUsersById = useCallback(
    async (id: string, limit = null, offset = 0) => {
      let obj = {
        data: null,
        error: null,
        meta: null,
        status: null,
      };

      try {
        registerPendingRequest();
        const jwtToken = (await Auth.currentSession())
          .getIdToken()
          .getJwtToken();
        const result = await httpGet(`${USERS_FEE_ENDPOINT}`, jwtToken, {
          userId: id,
          limit,
          offset,
        });

        obj = {
          data: result.data,
          error: null,
          meta: null,
          status: result.status,
        };
      } catch (err) {
        obj = {
          data: null,
          error: err,
          meta: null,
          status: null,
        };
      } finally {
        unregisterPendingRequest();
      }

      return obj;
    },
    [registerPendingRequest, unregisterPendingRequest],
  );

  const patchFeeUserById = useCallback(
    async (id: string, payload: any) => {
      let obj = {
        data: null,
        error: null,
        meta: null,
        status: null,
      };

      try {
        registerPendingRequest();
        const jwtToken = (await Auth.currentSession())
          .getIdToken()
          .getJwtToken();
        const result = await httpPatch(
          `${USERS_FEE_ENDPOINT}`,
          jwtToken,
          payload,
          id,
        );

        obj = {
          data: result.data,
          error: null,
          meta: null,
          status: result.status,
        };
      } catch (err) {
        obj = {
          data: null,
          error: err,
          meta: null,
          status: null,
        };
      } finally {
        unregisterPendingRequest();
      }

      return obj;
    },
    [registerPendingRequest, unregisterPendingRequest],
  );

  return (
    <UserFeeContext.Provider
      value={{
        getFeeUsers,
        getFeeUsersById,
        patchFeeUserById,
      }}
      {...props}
    />
  );
}
const useUserFee = () => React.useContext(UserFeeContext);

export { UserFeeProvider, useUserFee };
